import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'lucide-react';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import type { Message } from '@/types/message-types';

interface MediaStickerProps {
  message: Message;
  tenantId: string;
}

export function MediaSticker({ message, tenantId }: MediaStickerProps) {
  const { t } = useTranslation('conversations');
  const [stickerUrl, setStickerUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown>>({});

  console.log('🎭 MediaSticker component rendered!', { 
    messageId: message.id, 
    tenantId, 
    messageType: message.message_type,
    hasWhatsappMessageId: !!message.external_id,
    metadataType: typeof message.metadata,
    metadata: message.metadata
  });

  useEffect(() => {
    const loadSticker = async () => {
      console.log('🎭 MediaSticker: Starting sticker load process', {
        messageId: message.id,
        tenantId,
        metadata: message.metadata
      });

      const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
      console.log('🎭 MediaSticker: Extracted media ID:', mediaId);
      
      // Set debug info for display
      setDebugInfo({
        tenantId,
        mediaId,
        hasWhatsappMessageId: !!message.external_id,
        metadataType: typeof message.metadata,
        metadata: message.metadata
      });
      
      if (!mediaId) {
        console.log('❌ MediaSticker: No media ID found in message');
        setError('No media ID found');
        setLoading(false);
        return;
      }

      try {
        console.log('🎭 MediaSticker: Calling downloadAndGetMediaUrl with:', {
          tenantId,
          mediaId,
          mediaType: 'sticker',
          messageId: message.id
        });

        const result = await whatsappMediaService.downloadAndGetMediaUrl(
          tenantId, 
          mediaId, 
          'sticker',
          message.id
        );

        console.log('🎭 MediaSticker: Download result:', result);

        if (result.success) {
          console.log('✅ MediaSticker: Successfully got sticker URL:', result.fileUrl);
          
          // Test the URL by creating a test image to see if it loads
          if (result.fileUrl) {
            const testImg = new Image();
            testImg.onload = () => {
              console.log('✅ MediaSticker: URL is accessible:', result.fileUrl);
            };
            testImg.onerror = (e) => {
              console.error('❌ MediaSticker: URL failed to load:', result.fileUrl, e);
              setError('Image URL failed to load');
            };
            testImg.src = result.fileUrl;
            
            setStickerUrl(result.fileUrl);
          } else {
            setError('No URL returned from media service');
          }
        } else {
          console.log('❌ MediaSticker: Failed to load sticker:', result.error);
          setError(result.error || 'Failed to load sticker');
        }
      } catch (err) {
        console.error('❌ MediaSticker: Exception during sticker load:', err);
        setError('Failed to load sticker');
      } finally {
        setLoading(false);
      }
    };

    loadSticker();
  }, [message, tenantId]);

  if (loading) {
    return (
      <div className="flex flex-col items-start gap-2 p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Loader className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">{t('media.loadingSticker')}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          <div>Tenant ID: {debugInfo.tenantId || 'undefined'}</div>
          <div>Media ID: {debugInfo.mediaId || 'not found'}</div>
          <div>WhatsApp ID: {debugInfo.hasWhatsappMessageId ? '✅' : '❌'}</div>
        </div>
      </div>
    );
  }

  if (error || !stickerUrl) {
    const errorMessage = error || (!whatsappMediaService.extractMediaIdFromMessage(message) ? 'No media ID found' : 'Failed to load');
    return (
      <div className="flex flex-col items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20 max-w-sm">
        <div className="flex items-center gap-2">
          <span className="text-lg">🎭</span>
          <span className="text-sm font-medium">{t('media.stickerErrorTitle')}</span>
        </div>
        <div className="text-xs text-destructive space-y-1 w-full">
          <div className="break-words">Error: {errorMessage}</div>
          <div className="break-all">Tenant ID: {debugInfo.tenantId || 'undefined'}</div>
          <div className="break-all">Media ID: {debugInfo.mediaId || 'not found'}</div>
          <div>WhatsApp ID: {debugInfo.hasWhatsappMessageId ? '✅' : '❌'}</div>
          <div>Metadata Type: {debugInfo.metadataType}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="inline-block">
      <img
        src={stickerUrl}
        alt="Sticker"
        className="max-w-32 max-h-32 object-contain"
        style={{ imageRendering: 'pixelated' }}
        onLoad={() => console.log('✅ MediaSticker: Image loaded successfully')}
        onError={(e) => {
          console.error('❌ MediaSticker: Image failed to load', e);
          setError('Image failed to load');
        }}
      />
    </div>
  );
}